import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenHeader } from '../components/layout/ScreenHeader';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import {
  useGetTrainerProfileQuery,
  useUpdateTrainerProfileMutation,
} from '../store/coachApi';

const DEFAULT_WORKING_HOURS = { start: '09:00', end: '21:00' };

type SettingsFormProps = {
  initialName: string;
  initialEmail: string;
  initialWorkingHours: { start: string; end: string };
  loadError: string;
};

const SettingsForm = ({
  initialName,
  initialEmail,
  initialWorkingHours,
  loadError,
}: SettingsFormProps) => {
  const { t } = useTranslation();
  const navigate = useTransitionNavigate();
  const [updateProfile, { isLoading: saving }] = useUpdateTrainerProfileMutation();
  const [error, setError] = useState('');
  const [name, setName] = useState(initialName);
  const email = initialEmail;
  const [workingHours, setWorkingHours] = useState(initialWorkingHours);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await updateProfile({ name, workingHours }).unwrap();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось сохранить настройки'));
    }
  };

  return (
    <div className="page-container space-y-8">
      <ScreenHeader title={t('Настройки')} onBack={() => navigate('/profile')} backLabel={t('Назад')} />

      <div className="card max-w-xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          {(error || loadError) && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-600 rounded-2xl text-center text-sm font-medium">
              {error || loadError}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-800 ml-1">{t('Имя')}</label>
            <input
              type="text"
              className="input-field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-slate-800 ml-1">{t('Email')}</label>
            <input type="email" className="input-field" value={email} disabled />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-slate-800 ml-1">{t('Рабочие часы')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="section-label ml-1">
                  {t('Начало')}
                </label>
                <input
                  type="time"
                  className="input-field"
                  value={workingHours.start}
                  onChange={(e) =>
                    setWorkingHours((prev) => ({ ...prev, start: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="section-label ml-1">
                  {t('Конец')}
                </label>
                <input
                  type="time"
                  className="input-field"
                  value={workingHours.end}
                  onChange={(e) =>
                    setWorkingHours((prev) => ({ ...prev, end: e.target.value }))
                  }
                  required
                />
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button type="submit" className="btn-primary w-full sm:w-auto" disabled={saving}>
              {saving ? t('Сохранение...') : t('Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const { data: profile, error: profileError, isLoading } = useGetTrainerProfileQuery();

  if (isLoading) {
    return (
      <div className="page-container flex justify-center py-24">
        <div className="spinner h-10 w-10" />
      </div>
    );
  }

  const loadError = profileError
    ? getApiErrorMessage(profileError, 'Не удалось загрузить настройки')
    : '';

  return (
    <SettingsForm
      initialName={profile?.name ?? ''}
      initialEmail={profile?.email ?? ''}
      initialWorkingHours={profile?.workingHours ?? DEFAULT_WORKING_HOURS}
      loadError={loadError}
    />
  );
};

export default SettingsPage;
